import { ApiRoutes, PUBLIC_API_PREFIX } from "@engo/shared";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { EngoLogo } from "../../components/brand/EngoLogo.js";
import { GlassCard } from "../../components/ui/GlassCard.js";
import { GradientLink } from "../../components/ui/GradientLink.js";
import { durations, easing } from "../../lib/motion.js";
import { api } from "../../lib/api.js";
import { Onboarding } from "./Onboarding.js";

const ONBOARDING_SEEN_KEY = "engo_onboarding_seen";

export function LoginPage() {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [showOnboarding, setShowOnboarding] = useState(
    () => !window.localStorage.getItem(ONBOARDING_SEEN_KEY),
  );
  const { data, isPending, isSuccess } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => api.me(),
    retry: false,
  });

  if (isSuccess && data) {
    return <Navigate to="/" replace />;
  }

  function completeOnboarding() {
    window.localStorage.setItem(ONBOARDING_SEEN_KEY, "1");
    setShowOnboarding(false);
  }

  const initial = reduceMotion ? false : { opacity: 0, y: 12 };
  const animate = reduceMotion ? false : { opacity: 1, y: 0 };

  return (
    <div className="relative min-h-screen">
      <div className="flex min-h-screen flex-col items-center justify-center px-4 py-16">
        <motion.div
          className="w-full max-w-md"
          initial={initial}
          animate={animate}
          transition={{ duration: durations.page, ease: easing }}
        >
          {showOnboarding ? (
            <Onboarding onComplete={completeOnboarding} />
          ) : (
            <GlassCard className="text-center">
              <div className="flex flex-col items-center gap-3">
                <EngoLogo size={56} />
                <h1 className="font-display text-3xl font-semibold text-[var(--text-primary)]">
                  Engo
                </h1>
                <p className="text-sm font-medium text-[var(--accent)]">{t("brand.tagline")}</p>
              </div>
              <p className="mt-4 text-[var(--text-secondary)]">{t("login.subtitle")}</p>
              <div className="mt-8 flex flex-col gap-3">
                {isPending ? (
                  <p className="text-sm text-[var(--text-muted)]">{t("login.checkingSession")}</p>
                ) : (
                  <GradientLink
                    href={`${PUBLIC_API_PREFIX}${ApiRoutes.authGoogleStart}`}
                    className="w-full justify-center"
                  >
                    {t("login.continueWithGoogle")}
                  </GradientLink>
                )}
                <p className="text-xs text-[var(--text-muted)]">{t("login.disclaimer")}</p>
              </div>
            </GlassCard>
          )}
        </motion.div>
      </div>
    </div>
  );
}
